import * as THREE from "three";
import { createExplosion } from "../effects/Explosion";
import { handleProjectileHit } from "../damage";
import { Tank } from "../Tank";

const PROJECTILE_CONFIG = {
  SPEED: 2.0,
  RANGE: 10.0,
};

// [NEW] Shared geometries and materials
const sharedProjectileGeometry = new THREE.SphereGeometry(0.2, 8, 8);
const sharedProjectileMaterial = new THREE.MeshPhongMaterial({
  color: 0xffff00,
  emissive: 0xff9900,
  emissiveIntensity: 0.5,
  shininess: 30,
});

const sharedSmokeGeometry = new THREE.SphereGeometry(0.05, 8, 8);
const sharedSmokeMaterial = new THREE.MeshBasicMaterial({
  color: 0xdddddd,
  transparent: true,
  opacity: 0.4,
});

// Perf: a PointLight per projectile forces the forward renderer to evaluate an
// extra light in EVERY shader while it flies. An additive glow sprite looks the
// same (or better) and costs one cheap quad with no lighting impact.
let sharedGlowTexture: THREE.CanvasTexture | null = null;

function getSharedGlowTexture(): THREE.CanvasTexture {
  if (!sharedGlowTexture) {
    const canvas = document.createElement("canvas");
    canvas.width = 64;
    canvas.height = 64;
    const ctx = canvas.getContext("2d")!;
    const gradient = ctx.createRadialGradient(32, 32, 2, 32, 32, 32);
    gradient.addColorStop(0, "rgba(255, 255, 180, 1)");
    gradient.addColorStop(0.35, "rgba(255, 200, 80, 0.55)");
    gradient.addColorStop(1, "rgba(255, 150, 0, 0)");
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 64, 64);
    sharedGlowTexture = new THREE.CanvasTexture(canvas);
  }
  return sharedGlowTexture;
}

let sharedGlowMaterial: THREE.SpriteMaterial | null = null;

function getSharedGlowMaterial(): THREE.SpriteMaterial {
  if (!sharedGlowMaterial) {
    sharedGlowMaterial = new THREE.SpriteMaterial({
      map: getSharedGlowTexture(),
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
  }
  return sharedGlowMaterial;
}

/**
 * Tworzy i animuje pocisk wystrzelony z podanej pozycji w podanym kierunku
 * @param scene Scena Three.js
 * @param startPosition Pozycja początkowa pocisku
 * @param direction Kierunek lotu pocisku
 */
export function fireProjectile(
  scene: THREE.Scene,
  startPosition: THREE.Vector3,
  direction: THREE.Vector3,
  sourcePlayerId?: string,
) {
  // Tworzenie pocisku
  // [CHANGED] Use shared geometry and material
  const projectile = new THREE.Mesh(sharedProjectileGeometry, sharedProjectileMaterial);

  // Ustawienie pozycji początkowej
  projectile.position.copy(startPosition);

  // Dodanie pocisku do sceny
  scene.add(projectile);

  // Efekt poświaty pocisku - tani sprite zamiast PointLight (zero kosztu oświetlenia)
  const projectileGlow = new THREE.Sprite(getSharedGlowMaterial());
  projectileGlow.scale.setScalar(1.4);
  projectile.add(projectileGlow);

  // Parametry pocisku
  const projectileSpeed = PROJECTILE_CONFIG.SPEED;
  const gravity = -0.02; // Grawitacja
  let time = 0;

  // Funkcja tworząca efekt dymu za pociskiem
  const createSmokeTrail = () => {
    // [CHANGED] Use shared geometry
    const smokeGeometry = sharedSmokeGeometry;
    // [CHANGED] Clone shared material because opacity is animated
    const smokeMaterial = sharedSmokeMaterial.clone();

    const smoke = new THREE.Mesh(smokeGeometry, smokeMaterial);
    smoke.position.copy(projectile.position);
    scene.add(smoke);

    // Animacja dymu
    let smokeTime = 0;
    const animateSmoke = () => {
      smokeTime += 0.016;
      smoke.scale.set(1 + smokeTime, 1 + smokeTime, 1 + smokeTime);
      smoke.material.opacity = Math.max(0, 0.4 - smokeTime * 0.4);

      if (smokeTime > 1) {
        scene.remove(smoke);
        smokeMaterial.dispose();
        return;
      }

      requestAnimationFrame(animateSmoke);
    };

    animateSmoke();
  };

  // Zapisz początkowy kierunek jako wektor prędkości
  const velocity = direction.clone().multiplyScalar(projectileSpeed);

  // Animacja lotu pocisku
  const animateProjectile = () => {
    time += 0.016; // ~60fps

    // Zastosuj grawitację do składowej Y prędkości
    velocity.y += gravity;

    // Aktualizacja pozycji na podstawie prędkości
    projectile.position.add(velocity);

    /*
    // Log pozycji pocisku co 10 klatek
    if (Math.floor(time * 60) % 10 === 0) {
      console.log("Projectile position at time " + time.toFixed(2) + ":", {
        x: projectile.position.x.toFixed(2),
        y: projectile.position.y.toFixed(2),
        z: projectile.position.z.toFixed(2),
        velocity: {
          x: velocity.x.toFixed(2),
          y: velocity.y.toFixed(2),
          z: velocity.z.toFixed(2),
        },
      });
    }
      */

    // Dodaj efekt dymu co kilka klatek (rzadziej - ~9/s zamiast ~18/s, ten sam wygląd)
    if (Math.random() > 0.85) {
      createSmokeTrail();
    }

    // Check for tank collisions
    const tanks = window.gameState?.tanks ?? [];

    const collisionRadius = 3;
    const collisionRadiusSq = collisionRadius * collisionRadius;
    const hitTank = tanks.find((tankInstance) => projectile.position.distanceToSquared(tankInstance.mesh.position) < collisionRadiusSq);

    // Handle tank hit
    if (hitTank) {
      // Get the Tank instance from the mesh's userData
      const tankInstance = hitTank as Tank;

      if (tankInstance) {
        // Przekazujemy sourcePlayerId do handleProjectileHit aby poprawnie przypisać kille
        handleProjectileHit(projectile.position, tankInstance, sourcePlayerId);

        // Usuwamy event trafienia pociskiem w billboard, ponieważ interakcja z billboardami odbywa się tylko poprzez kliknięcia myszką
        // document.dispatchEvent(new CustomEvent('projectile:hit', {
        //   detail: {
        //     position: projectile.position.clone(),
        //     direction: direction.clone(),
        //     playerId: sourcePlayerId || 'unknown'
        //   }
        // }));

        // Create explosion effect at hit position
        createExplosion(scene, projectile.position);
        // Remove projectile (glow sprite is a child, removed together)
        scene.remove(projectile);
      } else {
        console.error("Tank hit but no Tank instance found in userData:", hitTank);
      }

      // Remove projectile
      scene.remove(projectile);
      return;
    }

    // Sprawdzenie czy pocisk uderzył w ziemię
    if (projectile.position.y <= 0) {
      // Ustaw pozycję wybuchu na poziomie ziemi
      const explosionPosition = projectile.position.clone();
      explosionPosition.y = 0;

      /*
      console.log("Projectile hit ground at:", {
        x: explosionPosition.x.toFixed(2),
        y: explosionPosition.y.toFixed(2),
        z: explosionPosition.z.toFixed(2),
        flightTime: time.toFixed(2),
      });
      */

      // Usuwamy event trafienia pociskiem w billboard, ponieważ interakcja z billboardami odbywa się tylko poprzez kliknięcia myszką
      // document.dispatchEvent(new CustomEvent('projectile:hit', {
      //   detail: {
      //     position: projectile.position.clone(),
      //     direction: direction.clone(),
      //     playerId: sourcePlayerId || 'unknown'
      //   }
      // }));

      // Efekt wybuchu
      createExplosion(scene, explosionPosition);
      scene.remove(projectile);
      return;
    }

    // Sprawdzenie maksymalnego czasu lotu
    if (time > PROJECTILE_CONFIG.RANGE / projectileSpeed) {
      // Usuwamy event trafienia pociskiem w billboard, ponieważ interakcja z billboardami odbywa się tylko poprzez kliknięcia myszką
      // document.dispatchEvent(new CustomEvent('projectile:hit', {
      //   detail: {
      //     position: projectile.position.clone(),
      //     direction: direction.clone(),
      //     playerId: sourcePlayerId || 'unknown'
      //   }
      // }));

      scene.remove(projectile);
      return;
    }

    requestAnimationFrame(animateProjectile);
  };

  // Rozpocznij animację
  animateProjectile();
}
